import { useState, useEffect } from 'react';
import { useIsElectron, useElectronAPI } from '@/context/ElectronContext';
import { getFileModificationTimeISO } from '@/common/utils/filePathUtils';

interface FileInput {
  fileName: string;
  uploadedAt: string;
}

/**
 * Hook to fetch and manage file modification times from the file system
 * @param files - Array of files to get modification times for
 * @param projectDirPath - Base directory path for the project
 * @returns Map of fileName -> modification time ISO string
 */
export function useFileModificationTimes(
  files: FileInput[],
  projectDirPath: string | null | undefined
): Map<string, string> {
  const [fileModTimes, setFileModTimes] = useState<Map<string, string>>(
    new Map()
  );
  const isElectron = useIsElectron();
  const electronAPI = useElectronAPI();

  useEffect(() => {
    if (!projectDirPath || files.length === 0) {
      setFileModTimes(new Map());
      return;
    }

    const fetchModTimes = async () => {
      const modTimeMap = new Map<string, string>();

      await Promise.all(
        files.map(async (file) => {
          const filePath = `${projectDirPath}/${file.fileName}`;
          const modTimeISO = await getFileModificationTimeISO(
            filePath,
            file.uploadedAt,
            electronAPI,
            isElectron
          );
          modTimeMap.set(file.fileName, modTimeISO);
        })
      );

      setFileModTimes(modTimeMap);
    };

    fetchModTimes();
  }, [files, projectDirPath, isElectron, electronAPI]);

  return fileModTimes;
}
